import { forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import type { TimelineEventProps } from './types';

const TimelineEvent = forwardRef<
  HTMLDivElement,
  TimelineEventProps & {
    renderIndicator?: (event: any, index: number) => React.ReactNode;
  }
>(({ event, index, renderIndicator }, ref) => {
  const formatDate = (dateInput: string | Date | null | undefined) => {
    if (!dateInput || dateInput === 'present') {
      return 'Present';
    }
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'work':
        return '💼';
      case 'education':
        return '🎓';
      case 'life':
        return '🌟';
      case 'test':
        return '🧪';
      case 'asd':
        return '🎯';
      default:
        return '📅';
    }
  };

  const CompanyLogo = ({ company }: { company: any }) => {
    if (!company?.logo_url) {
      return (
        <div className='timeline-event-company-logo'>
          <span className='timeline-event-company-initial'>
            {company?.name?.charAt(0) || 'C'}
          </span>
        </div>
      );
    }

    return (
      <img
        src={company.logo_url}
        alt={company.name}
        className='timeline-event-company-logo'
      />
    );
  };

  const PlatformIcon = ({ platform }: { platform: string }) => {
    const getIcon = (platform: string) => {
      switch (platform.toLowerCase()) {
        case 'github':
          return (
            <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 24 24'>
              <path d='M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z' />
            </svg>
          );
        case 'web':
          return (
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9'
              />
            </svg>
          );
        default:
          return (
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
              />
            </svg>
          );
      }
    };

    return <div className='platform-icon'>{getIcon(platform)}</div>;
  };

  const ProjectWithLinks = ({ project }: { project: any }) => {
    return (
      <div className='timeline-event-project'>
        <div className='timeline-event-project-card'>
          <div className='timeline-event-project-header'>
            <div className='timeline-event-project-title'>{project.title}</div>
            {project.start_date && (
              <div className='timeline-event-project-date'>
                {new Date(project.start_date).toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })}
                {!project.end_date ? (
                  <>
                    {' - '}
                    <span className='font-bold'>Ongoing</span>
                  </>
                ) : (
                  project.end_date && (
                    <>
                      {' '}
                      -{' '}
                      {new Date(project.end_date).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })}
                    </>
                  )
                )}
              </div>
            )}
          </div>
          <p className='timeline-event-project-description'>
            {project.description}
          </p>

          {project.techStack && project.techStack.length > 0 && (
            <div className='timeline-event-project-tech'>
              {project.techStack.map((tech: string, techIndex: number) => (
                <span
                  key={techIndex}
                  className='timeline-event-project-tech-tag'
                >
                  {tech}
                </span>
              ))}
            </div>
          )}

          {project.links && project.links.length > 0 && (
            <div className='timeline-event-project-links'>
              {project.links.map((link: any) => (
                <a
                  key={link.id}
                  href={link.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='timeline-event-project-link'
                  title={link.platform}
                >
                  <PlatformIcon platform={link.platform} />
                </a>
              ))}
            </div>
          )}

          {(!project.links || project.links.length === 0) &&
            project.live_url && (
              <div className='timeline-event-project-links'>
                <a
                  href={project.live_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='timeline-event-project-live-link'
                >
                  View Project
                  <svg
                    className='w-3 h-3 ml-1'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                    />
                  </svg>
                </a>
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <div ref={ref} className='timeline-event'>
      {renderIndicator ? (
        <div className='timeline-event-indicator'>
          {renderIndicator(event, index)}
        </div>
      ) : (
        <div
          className={`timeline-event-dot timeline-event-dot-${
            event.type || event.category
          }`}
        >
          <div className='timeline-event-dot-glow'></div>
        </div>
      )}

      <div className='timeline-event-date'>
        <div className='timeline-event-year'>
          {new Date(event.start_date || event.date).getFullYear()}
        </div>
        <div className='timeline-event-month'>
          {new Date(event.start_date || event.date).toLocaleString('default', {
            month: 'short',
          })}
        </div>
      </div>

      <div className='timeline-event-content'>
        <div className='timeline-event-card'>
          <div className='timeline-event-header'>
            <div className='timeline-event-type-badge'>
              {(event.type || event.category).charAt(0).toUpperCase() +
                (event.type || event.category).slice(1)}
            </div>

            <div className='timeline-event-date-badge'>
              {formatDate(event.start_date || event.date)}
              {event.type === 'work' &&
                event.end_date !== event.start_date &&
                event.end_date !== 'present' && (
                  <> - {formatDate(event.end_date)}</>
                )}
              {event.type === 'work' &&
                event.end_date === 'present' &&
                ' - Present'}
            </div>
          </div>

          <div className='timeline-event-title-section'>
            <div className='timeline-event-icon'>
              {event.type === 'work' && event.company ? (
                <CompanyLogo company={event.company} />
              ) : (
                <span className='timeline-event-type-icon'>
                  {getTypeIcon(event.type || event.category)}
                </span>
              )}
            </div>
            <h3 className='timeline-event-title'>
              {event.heading || event.title}
            </h3>
          </div>

          <div className='timeline-event-description'>
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className='timeline-event-description-h1'>{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className='timeline-event-description-h2'>{children}</h2>
                ),
                h3: ({ children }) => (
                  <h3 className='timeline-event-description-h3'>{children}</h3>
                ),
                p: ({ children }) => (
                  <p className='timeline-event-description-p'>{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className='timeline-event-description-ul'>{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className='timeline-event-description-ol'>{children}</ol>
                ),
                li: ({ children }) => (
                  <li className='timeline-event-description-li'>{children}</li>
                ),
                blockquote: ({ children }) => (
                  <blockquote className='timeline-event-description-blockquote'>
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code className='timeline-event-description-code'>
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className='timeline-event-description-pre'>
                    {children}
                  </pre>
                ),
                strong: ({ children }) => (
                  <strong className='timeline-event-description-strong'>
                    {children}
                  </strong>
                ),
                em: ({ children }) => (
                  <em className='timeline-event-description-em'>{children}</em>
                ),
                a: ({ children, href }) => (
                  <a
                    href={href}
                    className='timeline-event-description-a'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {children}
                  </a>
                ),
                img: ({ src, alt }) => (
                  <img
                    src={src}
                    alt={alt}
                    className='timeline-event-description-img'
                  />
                ),
              }}
            >
              {event.description || ''}
            </ReactMarkdown>
          </div>

          {event.projects && event.projects.length > 0 && (
            <div className='timeline-event-projects'>
              <h4 className='timeline-event-projects-title'>Projects:</h4>
              <div className='timeline-event-projects-list'>
                {event.projects.map((project: any) => (
                  <ProjectWithLinks key={project.id} project={project} />
                ))}
              </div>
            </div>
          )}

          {event.tech_stack_summary && event.tech_stack_summary.length > 0 && (
            <div className='timeline-event-tech-stack'>
              <h4 className='timeline-event-tech-stack-title'>
                <span className='timeline-event-tech-stack-icon'>⚡</span>
                Tech Stack
              </h4>
              <div className='timeline-event-tech-stack-tags'>
                {event.tech_stack_summary.map(
                  (tech: string, techIndex: number) => (
                    <span
                      key={techIndex}
                      className='timeline-event-tech-stack-tag'
                    >
                      {tech}
                    </span>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

TimelineEvent.displayName = 'TimelineEvent';

export default TimelineEvent;
